// src/components/LanguageSelector/index.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { SvgIcon } from '../../common/SvgIcon';
import { LanguageSwitchContainer, LanguageSwitch } from './styles';
import { supportedLangs } from '../../translation';

interface Language {
  code: string;
  name: string;
  icon: string;
}

const languages: Language[] = [
  { code: 'en', name: 'English', icon: 'flags/GB.svg' },
  { code: 'da', name: 'Dansk', icon: 'flags/DK.svg' },
  { code: 'sv', name: 'Svenska', icon: 'flags/SE.svg' },
  { code: 'no', name: 'Norsk', icon: 'flags/NO.svg' },
  { code: 'nl', name: 'Nederlands', icon: 'flags/NL.svg' },
  { code: 'de', name: 'Deutsch', icon: 'flags/DE.svg' },
  { code: 'es', name: 'Español', icon: 'flags/ES.svg' },
  { code: 'it', name: 'Italiano', icon: 'flags/IT.svg' },
  { code: 'fr', name: 'Français', icon: 'flags/FR.svg' }
];

const LanguageSelector: React.FC = () => {
  const { i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  
  const handleLanguageChange = (languageCode: string) => {
    // First, change the language in i18n
    i18n.changeLanguage(languageCode);
    localStorage.setItem('i18nextLng', languageCode);
    
    // Get the current path and segments
    const pathSegments = location.pathname.split('/').filter(Boolean);
    
    // Determine if the first segment is a language code
    const isFirstSegmentLanguage = supportedLangs.includes(pathSegments[0]);
    
    // Create the new URL
    let newUrl: string;
    
    // Special case for English (default language)
    if (languageCode === 'en') {
      // If we already have a language prefix, remove it
      if (isFirstSegmentLanguage) {
        // Get the path without the language prefix
        newUrl = '/' + pathSegments.slice(1).join('/');
        // If the URL becomes just "/", that's fine
        if (newUrl === '') newUrl = '/';
      } else {
        // No language prefix, keep the current path
        newUrl = location.pathname;
      }
    } else {
      // For non-English languages
      if (isFirstSegmentLanguage) {
        // Replace the existing language prefix
        newUrl = '/' + languageCode + '/' + pathSegments.slice(1).join('/');
      } else {
        // Add the language prefix to the current path
        newUrl = '/' + languageCode + location.pathname;
      }
    }
    
    // Navigate to the new URL
    history.push(newUrl);
  };

  // Function to determine the currently active language
  const getActiveLanguage = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    
    // If first segment is a language code, use that
    if (supportedLangs.includes(pathSegments[0])) {
      return pathSegments[0];
    }
    
    // Otherwise use the language from i18n
    return i18n.language;
  };

  const activeLanguage = getActiveLanguage();

  return (
    <LanguageSwitchContainer>
      {languages.map(({ code, icon }) => (
        <LanguageSwitch 
          key={code} 
          onClick={() => handleLanguageChange(code)}
          className={activeLanguage === code ? 'active' : ''}
          title={languages.find(lang => lang.code === code)?.name || code}
        >
          <SvgIcon src={icon} width="24px" height="24px" />
        </LanguageSwitch>
      ))}
    </LanguageSwitchContainer>
  );
};

export default LanguageSelector;